import React, { useState } from 'react';
import { AppSectionHeader, ButtonPrimary } from '../../components-v2/shared';
import Resource from '../../store/Resource';
import { CLiQCompanySMSConfig, TwilioNumber } from '../../types';
import { CompanyDetailsState, EnablingSMSStatus } from './CompanyDetailsForm.helpers';
import { DateTime } from 'luxon';
import { Button, Modal, ModalActions, ModalHeader } from '@lambdacurry/component-library';

const isTelnyxSid = (sid?: string) => Boolean(sid && sid.startsWith('telnyx-'));

export const CompanyDetailsSMSNumber: React.FC<{
    isCreateCompanyForm: boolean;
    state: CompanyDetailsState;
    twilioNumbers: Resource<TwilioNumber>;
    masterId?: number;
    cliqDashboardUrl?: string;
    smsConfigurations: CLiQCompanySMSConfig[];
    fetchSmsConfigurations: () => Promise<CLiQCompanySMSConfig[]>;
    syncSmsConfigurations: () => Promise<boolean>;
}> = ({
    isCreateCompanyForm,
    state,
    twilioNumbers,
    masterId,
    cliqDashboardUrl,
    smsConfigurations,
    fetchSmsConfigurations,
    syncSmsConfigurations
}) => {
        const [isFetchingConfigurations, setIsFetchingConfigurations] = useState(false);
        const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
        const [replaceCopy, setReplaceCopy] = useState<{ title: string; body: string; action: string }>({
            title: 'Replace Twilio SMS profile?',
            body: 'A Telnyx-enabled CLiQ Messages configuration matches an existing Twilio SMS number. Do you want to replace the Twilio profile with the Telnyx configuration?',
            action: 'Yes, replace profile'
        });
        const liveTwilioNumber = twilioNumbers.values.find(number => number.active && !isTelnyxSid(number.sid));
        const pendingTwilioReleases = twilioNumbers.values.filter(
            number => !isTelnyxSid(number.sid) && !number.active && Boolean(number.deleted_at)
        );
        const normalizePhoneNumber = (phoneNumber: string) => {
            const digits = phoneNumber.replace(/\D/g, '');
            return digits.length === 10 ? `+1${digits}` : `+${digits}`;
        };

        const telnyxConfigurations = smsConfigurations.filter(configuration => {
            if (
                configuration.master_id !== masterId ||
                configuration.provider !== 'sms_telnyx' ||
                !configuration.config ||
                !configuration.config.phone_number
            ) {
                return false;
            }

            const configuredNumber = normalizePhoneNumber(configuration.config.phone_number);
            return twilioNumbers.values.some(
                number =>
                    number.active &&
                    normalizePhoneNumber(number.phone_num_full) === configuredNumber &&
                    isTelnyxSid(number.sid)
            );
        });

        const hasMatchingTwilioNumber = (configurations: CLiQCompanySMSConfig[]) =>
            configurations.some(configuration => {
                const configuredNumber = normalizePhoneNumber(configuration.config.phone_number);
                const matchingNumber = twilioNumbers.values.find(
                    number =>
                        number.active &&
                        normalizePhoneNumber(number.phone_num_full) === configuredNumber
                );

                return Boolean(matchingNumber && !isTelnyxSid(matchingNumber.sid));
            });

        const hasDifferentLiveTwilioNumber = (configurations: CLiQCompanySMSConfig[]) => {
            if (!liveTwilioNumber) {
                return false;
            }

            return configurations.some(configuration => {
                const configuredNumber = normalizePhoneNumber(configuration.config.phone_number);
                return normalizePhoneNumber(liveTwilioNumber.phone_num_full) !== configuredNumber;
            });
        };

        const syncConfigurations = async () => {
            setIsFetchingConfigurations(true);
            await syncSmsConfigurations();
            setIsFetchingConfigurations(false);
        };

        const fetchConfigurations = async () => {
            setIsFetchingConfigurations(true);
            const configurations = await fetchSmsConfigurations();
            setIsFetchingConfigurations(false);

            const availableTelnyxConfigurations = configurations.filter(
                configuration =>
                    configuration.master_id === masterId &&
                    configuration.provider === 'sms_telnyx' &&
                    Boolean(configuration.config && configuration.config.phone_number)
            );

            if (!availableTelnyxConfigurations.length) {
                await syncConfigurations();
                return;
            }

            if (hasMatchingTwilioNumber(availableTelnyxConfigurations)) {
                setReplaceCopy({
                    title: 'Replace Twilio SMS profile?',
                    body:
                        'A Telnyx-enabled CLiQ Messages configuration matches an existing Twilio SMS number. Do you want to replace the Twilio profile with the Telnyx configuration?',
                    action: 'Yes, replace profile'
                });
                setIsReplaceModalOpen(true);
                return;
            }

            if (hasDifferentLiveTwilioNumber(availableTelnyxConfigurations)) {
                const nextNumber = availableTelnyxConfigurations[0].config.phone_number;
                setReplaceCopy({
                    title: 'Replace Twilio SMS number?',
                    body: `This company currently uses ${liveTwilioNumber!.phone_num_friendly}. Fetching from CLiQ will switch the live sender to ${nextNumber} and schedule ${liveTwilioNumber!.phone_num_friendly} for release from Twilio.`,
                    action: 'Yes, replace number'
                });
                setIsReplaceModalOpen(true);
                return;
            }

            await syncConfigurations();
        };

        return isCreateCompanyForm ? (
            <>
                <AppSectionHeader title="SMS Number" />
                <p className="text">Provision new SMS numbers in the CLiQ Dashboard within CLiQ Messages.</p>
            </>
        ) : (
            <>
                {state.enablingSMSStatus === EnablingSMSStatus.LOADING && (
                    <>
                        <AppSectionHeader title="SMS Number" />
                        <p className="text">Loading...</p>
                    </>
                )}
                {state.enablingSMSStatus !== EnablingSMSStatus.LOADING && (
                    <>
                        <AppSectionHeader title="SMS Number">
                            {!telnyxConfigurations.length && (<ButtonPrimary
                                onClick={fetchConfigurations}
                                disabled={!masterId || isFetchingConfigurations}
                            >
                                {isFetchingConfigurations ? 'Fetching...' : 'Fetch from CLiQ Messages'}
                            </ButtonPrimary>)}

                        </AppSectionHeader>
                        {liveTwilioNumber && (
                            <>
                                <p className="text">{liveTwilioNumber.phone_num_friendly}</p>
                            </>
                        )}
                        {pendingTwilioReleases.map(number => (
                            <p className="text" key={number.id}>
                                {number.phone_num_friendly} will be released from Twilio after{' '}
                                <span className="color-dark-red">
                                    {DateTime.fromISO(number.deleted_at).toLocaleString(DateTime.DATETIME_FULL)}
                                </span>
                            </p>
                        ))}
                        {telnyxConfigurations.length > 0 && (
                            <div className="company-details-sms-configurations">
                                <AppSectionHeader
                                    as="Header"
                                    title={`CLiQ Messages Telnyx Configurations (${telnyxConfigurations.length})`}
                                />
                                <div className="company-details-sms-configurations-list">
                                    {telnyxConfigurations.map(configuration => (
                                        <div key={configuration.config.id} className="company-details-sms-configuration">
                                            <div>
                                                <span className="company-details-sms-configuration-label">SMS Number</span>
                                                <p className="company-details-sms-configuration-value">
                                                    {configuration.config.phone_number}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="company-details-sms-configuration-label">
                                                    Messaging Profile GUID
                                                </span>
                                                <p className="company-details-sms-configuration-value">
                                                    {configuration.config.messaging_profile_id}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {twilioNumbers.values.filter(number => number.active).length === 0 &&
                            state.enablingSMSStatus === EnablingSMSStatus.WAITING && (
                                <>
                                    <p className="text">
                                        Provision a new SMS number in the CLiQ Dashboard within{' '}
                                        {cliqDashboardUrl ? (
                                            <a
                                                className="company-details-cliq-messages-link"
                                                href={cliqDashboardUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                CLiQ Messages ↗
                                            </a>
                                        ) : (
                                            'CLiQ Messages'
                                        )}
                                        , then use Fetch from CLiQ Messages to load it here.
                                    </p>
                                </>
                            )}
                    </>
                )}
                <Modal isOpen={isReplaceModalOpen} onAfterClose={() => setIsReplaceModalOpen(false)}>
                    <ModalHeader title={replaceCopy.title} />
                    <p className="text">{replaceCopy.body}</p>
                    <ModalActions>
                        <Button onClick={() => setIsReplaceModalOpen(false)}>No</Button>
                        <ButtonPrimary
                            onClick={async () => {
                                setIsReplaceModalOpen(false);
                                await syncConfigurations();
                            }}
                            disabled={isFetchingConfigurations}
                        >
                            {replaceCopy.action}
                        </ButtonPrimary>
                    </ModalActions>
                </Modal>
            </>
        );
    };
